'use client';

import CustomButton from '@/components/custom-button';
import CustomToast from '@/components/custom-toast';
import { randomuuid } from '@/lib/utils';
import { usePathname, useRouter } from '@/navigation';
import { SignalMethod, SocketEndpoint } from '@/signalR/enum';
import { useSocketListener } from '@/signalR/use-socket-listener';
import { useSocketStore } from '@/signalR/use-socket-store';
import { Info, RefreshCcw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef } from 'react';

const { sonner } = CustomToast;

/**
 * Hook để phát hiện sự thay đổi dữ liệu từ SignalR
 * và hiển thị thông báo cho người dùng.
 * Khi có sự thay đổi lớn, người dùng sẽ được
 * yêu cầu làm mới dữ liệu để đảm bảo tính chính xác.
 *
 *
 * @description {DEV}
 */
export const useSignalDetecting = () => {
    const _timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const pathname = usePathname();
    const router = useRouter();

    const sonnerIDRef = useRef<string | null>(null);
    const transDef = useTranslations('System');
    const hub = useSocketStore((x) => x.dictionaryHub[SocketEndpoint.hubs]);

    const onRenew = useCallback(() => {
        router.refresh();
        if (sonnerIDRef.current) {
            sonner.dismiss(sonnerIDRef.current);
            sonnerIDRef.current = null;
        }
    }, [router]);

    const onListenChangeEndpoint = useCallback(
        (data: any) => {
            if (sonnerIDRef.current) {
                sonner.dismiss(sonnerIDRef.current);
            }
            const sonnerId = randomuuid();
            sonnerIDRef.current = sonnerId;

            sonner.info(
                <div className='flex w-full items-stretch justify-between gap-x-2'>
                    <div className='flex gap-x-1'>
                        <Info size={18} className='mt-0.5 text-blue-500' />
                        <div>
                            <h6 className='text-sm font-medium'>
                                Dữ liệu có sự thay đổi lớn!
                            </h6>
                            <span className='text-xs text-gray-500'>
                                Cần đồng bộ lại dữ liệu để đảm bảo tính chính
                                xác.
                            </span>
                        </div>
                    </div>

                    <div className='flex items-center justify-center'>
                        <CustomButton.Root
                            size='sm'
                            className='h-8 bg-blue-500 text-sm text-white hover:bg-blue-400 hover:text-white'
                            icon={<RefreshCcw size={13} />}
                            onClick={onRenew}
                        >
                            {transDef('System_ReNew')}
                        </CustomButton.Root>
                    </div>
                </div>,
                {
                    style: { width: '400px' },
                    position: 'top-center',
                    duration: 6000,
                    closeButton: false,
                    id: sonnerId,
                }
            );
        },
        [transDef, onRenew]
    );

    /**
     * Invoke vị trí đứng của nhân viên hiện tại
     */
    const onTriggerSignalMessage = useCallback(
        async (pathname: string) => {
            const send_data = { pathname };

            if (!!hub) {
                // const result = await hub.invoke(
                //     SignalMethod.GetEndpointInfos,
                //     send_data
                // );
                // console.log({ result });
            }
        },
        [hub]
    );

    useEffect(() => {
        if (_timeoutRef.current) {
            clearTimeout(_timeoutRef.current);
        }
        _timeoutRef.current = setTimeout(() => {
            onTriggerSignalMessage(pathname);
        }, 1500);
    }, [onTriggerSignalMessage, pathname]);

    // useSocketListener({
    //     endPoint: SocketEndpoint.hubs,
    //     listener: {
    //         [SignalMethod.GetEndpointInfos]: onListenChangeEndpoint,
    //     },
    // });
};
